import { AnimatePresence, m, type Variants } from 'framer-motion';
import { Building2, Search, Server } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHierarchy, type FlatNode } from '../../hierarchy/HierarchyProvider';
import { Badge, EmptyState, Loading, Modal, StatusDot } from '../ui';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '../ui/sidebar';

/**
 * Selector rápido de nodos — el reemplazo del árbol Cliente→Sucursal→Nodo que
 * antes vivía siempre visible en el sidebar (`HierarchySidebar`, ya retirado).
 *
 * Esa lista crecía con cada nodo que se sumara al OCC sin ningún límite: un
 * cliente con 200 cajas hacía el sidebar tan largo como el propio parque. Este
 * componente no tiene ese problema porque no está siempre montado — es un
 * botón (mismo lugar y forma que el "team switcher" de shadcn-admin) que abre
 * una rejilla de tarjetas por cliente, con buscador, y se cierra en cuanto se
 * elige un nodo. El árbol no se resume ni se pagina: se busca.
 */

type ClientGroup = { clientName: string; nodes: FlatNode[] };

const groupByClient = (nodes: FlatNode[]): ClientGroup[] => {
  const order: string[] = [];
  const groups = new Map<string, FlatNode[]>();
  for (const node of nodes) {
    const list = groups.get(node.clientName);
    if (list) list.push(node);
    else {
      groups.set(node.clientName, [node]);
      order.push(node.clientName);
    }
  }
  return order.map((clientName) => ({ clientName, nodes: groups.get(clientName)! }));
};

const GRID_CONTAINER: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.03 } },
};
const GRID_ITEM: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.18, ease: 'easeOut' } },
};

export function NodeSwitcher() {
  const { nodes, loading } = useHierarchy();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Cmd/Ctrl+K en cualquier pantalla, no solo con el botón a la vista — igual
  // que el atajo Cmd/Ctrl+B de mostrar/ocultar el sidebar.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return nodes;
    return nodes.filter(
      (node) =>
        node.alias.toLowerCase().includes(needle) ||
        node.clientName.toLowerCase().includes(needle) ||
        node.branchName.toLowerCase().includes(needle),
    );
  }, [nodes, search]);

  const grouped = useMemo(() => groupByClient(filtered), [filtered]);
  const onlineCount = nodes.filter((node) => node.status === 'online').length;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" onClick={() => setOpen(true)}>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Server className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">Nodos</span>
              <span className="truncate text-xs opacity-70">
                {onlineCount}/{nodes.length} en línea
              </span>
            </div>
            <span className="ml-auto flex items-center gap-1 opacity-50 group-data-[collapsible=icon]:hidden">
              <Search className="size-3.5" />
              <kbd className="rounded border border-current/30 px-1 text-[10px]">⌘K</kbd>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>

      <Modal
        open={open}
        title="Saltar a un nodo"
        onClose={close}
        maxWidthClassName="max-w-4xl"
      >
        <div className="grid gap-4">
          <div className="relative">
            <Search className="pointer-events-none absolute inset-y-0 left-3 my-auto size-4 opacity-40" />
            <input
              autoFocus
              type="search"
              value={search}
              placeholder="Buscar por alias, cliente o sucursal…"
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-md bg-accent/40 py-2 pl-9 pr-3 text-sm shadow-sm outline-none focus:shadow-[0_0_0_2px_var(--ring)]"
            />
          </div>

          {loading && nodes.length === 0 ? (
            <Loading label="Cargando nodos…" />
          ) : grouped.length === 0 ? (
            <EmptyState title="Sin nodos que coincidan">
              Prueba con otro alias, cliente o sucursal.
            </EmptyState>
          ) : (
            <m.div
              variants={GRID_CONTAINER}
              initial="hidden"
              animate="show"
              // El gutter reservado evita que la barra de scroll se pinte encima
              // del texto de la última columna — sin él, el navegador la
              // superpone en vez de restarle sitio al contenido.
              className="grid max-h-[60vh] gap-3 overflow-y-auto pr-1 [scrollbar-gutter:stable] sm:grid-cols-2 lg:grid-cols-3"
            >
              <AnimatePresence initial={false}>
                {grouped.map(({ clientName, nodes: clientNodes }) => {
                  const clientOnline = clientNodes.filter((n) => n.status === 'online').length;
                  return (
                    <m.div
                      key={clientName}
                      variants={GRID_ITEM}
                      // `min-w-0` es lo que le falta a un hijo de grid para
                      // poder ser más angosto que su contenido: sin esto, un
                      // alias largo empuja el ancho de esta tarjeta más allá de
                      // su columna y se pinta encima de la columna vecina en
                      // vez de truncarse.
                      className="grid min-w-0 content-start gap-1 rounded-lg border border-black/5 bg-accent/20 p-2 dark:border-white/5"
                    >
                      <div className="flex items-center gap-1.5 px-1 pb-0.5">
                        <Building2 className="size-3.5 shrink-0 opacity-50" />
                        <span className="min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-wide opacity-70">
                          {clientName}
                        </span>
                        <Badge tone={clientOnline > 0 ? 'ok' : 'neutral'}>
                          {clientOnline}/{clientNodes.length}
                        </Badge>
                      </div>
                      <div className="grid gap-0.5">
                        {clientNodes.map((node) => (
                          <button
                            key={node.id}
                            type="button"
                            title={`${node.alias} · ${node.branchName}`}
                            onClick={() => {
                              navigate(`/nodes/${node.id}`);
                              close();
                            }}
                            className="flex min-w-0 items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-accent/60"
                          >
                            <StatusDot status={node.status} />
                            <span className="min-w-0 flex-1 truncate">{node.alias}</span>
                            <span className="max-w-[40%] shrink-0 truncate text-xs opacity-50">
                              {node.branchName}
                            </span>
                          </button>
                        ))}
                      </div>
                    </m.div>
                  );
                })}
              </AnimatePresence>
            </m.div>
          )}
        </div>
      </Modal>
    </>
  );
}
